import { add, differenceInMonths, isDate, startOfMonth, sub } from 'date-fns';
import { useMemo } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm, useWatch } from 'react-hook-form';
import { FaCaretDown, FaCaretLeft, FaCaretRight } from 'react-icons/fa';
import { useUpdateEffect } from 'react-use';
import { date, InferType, object } from 'yup';

import { Button, Datepicker, FieldWrapper, FormField } from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { useReportingFilterQueryParams } from '../utils/use-reporting-query-params';

const filterSchema = object({ date: date().required() });

type FormValues = InferType<typeof filterSchema>;
type FormPath = FieldPath<FormValues>;
type FormDefaultValues = DeepPartial<FormValues>;

export const ReportingMonthlyDateToolbar = () => {
  const [filterQueryParams, setFilterQueryParams] = useReportingFilterQueryParams();
  const dateQueryParam = useMemo(
    () => filterQueryParams.reportingMonth ?? startOfMonth(new Date()),
    [filterQueryParams.reportingMonth],
  );

  const defaultValues: FormDefaultValues = useMemo(() => ({ date: dateQueryParam }), [dateQueryParam]);

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(filterSchema),
  });

  const { control, setValue } = methods;
  const selectedDate = useWatch({ control: control, name: 'date' });

  const maxDate = startOfMonth(new Date()); // needs to be the first of the month (so the month of maxDate can be selected but nothing after it)

  const decreaseDate = () => {
    if (!isDate(selectedDate)) return;

    setValue('date', sub(selectedDate, { months: 1 }));
  };

  const increaseDate = () => {
    if (!isDate(selectedDate)) return;
    if (isDate(maxDate) && differenceInMonths(maxDate, selectedDate) <= 0) return;

    setValue('date', add(selectedDate, { months: 1 }));
  };

  useUpdateEffect(() => {
    setFilterQueryParams({
      reportingMonth: selectedDate,
    });
  }, [selectedDate]);

  return (
    <div className="flex items-center space-x-2">
      <FormProvider {...methods}>
        <form>
          <FormField<FormValues, FormPath>
            name="date"
            required
            render={({ field: { value, ...field } }) => (
              <FieldWrapper>
                <Datepicker
                  {...field}
                  selected={value}
                  dateFormat="MMM yyyy"
                  showMonthYearPicker
                  maxDate={maxDate}
                  hideIcon
                  className="h-12 !bg-gray-600 !border-none text-center cursor-pointer !pr-8 min-w-[224px]"
                />
                <FieldWrapper.EndAdornment>
                  <span className="text-secondary text-sm">
                    <FaCaretDown />
                  </span>
                </FieldWrapper.EndAdornment>
              </FieldWrapper>
            )}
          />
        </form>
      </FormProvider>

      <div className="flex space-x-0.5">
        <Button size="none" color="primary-dark" disabledVariant="dark" className="h-12 w-10" onClick={decreaseDate}>
          <FaCaretLeft />
        </Button>

        <Button
          size="none"
          color="primary-dark"
          disabledVariant="dark"
          className="h-12 w-10"
          onClick={increaseDate}
          disabled={isDate(selectedDate) && isDate(maxDate) && differenceInMonths(maxDate, selectedDate) <= 0}
        >
          <FaCaretRight />
        </Button>
      </div>
    </div>
  );
};
